import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { obtenerRolesPropios, seleccionarRolActivo, type ResultadoActivacionRol } from '@/lib/multiRolApi';
import { rolesElegiblesAdmin, type RolAsignado } from '@/lib/multiRol';

/**
 * Activa un rol (RPC `seleccionar_rol_activo`) y deja la sesión coherente:
 * perfil del AuthContext releído, menús/permisos recalculados y caches de
 * react-query invalidadas (los datos cargados bajo el rol anterior pasaron por
 * su RLS). Funciona sin `profile` todavía cargado, por eso lo usa el Login.
 */
export function useActivarRol() {
  const { refreshProfile, triggerPermissionRefresh } = useAuth();
  const queryClient = useQueryClient();

  return useCallback(
    async (rolId: number): Promise<ResultadoActivacionRol> => {
      const resultado = await seleccionarRolActivo(rolId);
      if (resultado.estado === 'ok') {
        // useAllowedMenus / useDynamicMenus / usePagePermissions no usan
        // react-query: se recalculan por profile.rol_id + permissionVersion.
        await refreshProfile();
        triggerPermissionRefresh();
        await queryClient.invalidateQueries();
      }
      return resultado;
    },
    [refreshProfile, triggerPermissionRefresh, queryClient],
  );
}

/**
 * Roles asignados al usuario en sesión, con nombre (para "Cambiar de rol").
 * `elegiblesAdmin` excluye los roles de host propio (3, 9, 23, 36, 43).
 */
export function useRolesAsignados(): {
  roles: RolAsignado[] | null;
  elegiblesAdmin: RolAsignado[];
} {
  const { profile } = useAuth();
  const email = profile?.email?.toLowerCase() ?? null;
  const rolActivo = profile?.rol_id ?? null;

  const { data } = useQuery({
    queryKey: ['roles_asignados', email, rolActivo],
    enabled: !!email,
    staleTime: 60_000,
    queryFn: () => obtenerRolesPropios(email as string, rolActivo),
  });

  const roles = data ?? null;
  return { roles, elegiblesAdmin: roles ? rolesElegiblesAdmin(roles) : [] };
}
